import { i18n } from '@/i18n'

/**
 * Traducción de errores `@hapi/boom` a algo que una persona pueda leer.
 *
 * El backend responde `{ statusCode, error, message }`. El campo `message` es
 * útil cuando el backend lo ha escrito a propósito ("La cuota de sesiones de
 * WhatsApp de tu plan está completa"), e inútil cuando es la frase por defecto
 * de Boom en inglés ("An internal server error occurred"). Se filtra por esa
 * lista y, si no aporta, se usa un mensaje propio traducido.
 */

/** Frases por defecto de Boom: no se muestran nunca al usuario. */
const BOOM_DEFAULT_MESSAGES = new Set([
  'bad request',
  'unauthorized',
  'payment required',
  'forbidden',
  'not found',
  'method not allowed',
  'not acceptable',
  'request entity too large',
  'unsupported media type',
  'unprocessable entity',
  'too many requests',
  'an internal server error occurred',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
  'internal server error',
])

/** Códigos HTTP → clave de i18n con el mensaje propio. */
const STATUS_KEYS = {
  400: 'errors.badRequest',
  401: 'errors.unauthorized',
  402: 'errors.paymentRequired',
  403: 'errors.forbidden',
  404: 'errors.notFound',
  405: 'errors.methodNotAllowed',
  409: 'errors.conflict',
  413: 'errors.payloadTooLarge',
  415: 'errors.unsupportedMedia',
  422: 'errors.validation',
  429: 'errors.rateLimited',
  500: 'errors.server',
  502: 'errors.badGateway',
  503: 'errors.unavailable',
  504: 'errors.gatewayTimeout',
}

/** Estados en los que reintentar tiene sentido. */
const RETRIABLE_STATUSES = new Set([0, 408, 425, 429, 500, 502, 503, 504])

/**
 * @typedef {object} AppError
 * @property {number} status  Código HTTP; 0 si la petición ni siquiera salió.
 * @property {string} kind    `network` | `timeout` | `canceled` | `http`
 * @property {string} message Mensaje ya traducido y presentable.
 * @property {boolean} retriable
 * @property {Record<string, string[]>|null} fields Errores por campo, si el backend los envía.
 * @property {unknown} cause  Error original, sólo para depuración.
 */

function translate(key, params) {
  // `i18n.global.t` funciona igual dentro y fuera de componentes.
  return i18n.global.t(key, params ?? {})
}

function isUsefulServerMessage(message) {
  if (typeof message !== 'string') return false
  const trimmed = message.trim()
  if (trimmed.length === 0 || trimmed.length > 400) return false
  return !BOOM_DEFAULT_MESSAGES.has(trimmed.toLowerCase())
}

/**
 * Normaliza cualquier fallo (de red, de Axios o de Boom) a un `AppError`.
 * @param {unknown} error
 * @param {{ fallbackKey?: string }} [options]
 * @returns {AppError}
 */
export function toAppError(error, options = {}) {
  if (error && error.__isAppError) return error

  const canceled =
    error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError'
  if (canceled) {
    return build({ status: 0, kind: 'canceled', message: '', retriable: false, cause: error })
  }

  const timedOut = error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT'
  if (timedOut) {
    return build({
      status: 0,
      kind: 'timeout',
      message: translate('errors.timeout'),
      retriable: true,
      cause: error,
    })
  }

  const response = error?.response
  if (!response) {
    return build({
      status: 0,
      kind: 'network',
      message: translate('errors.network'),
      retriable: true,
      cause: error,
    })
  }

  const status = response.status ?? 0
  const payload = response.data ?? {}
  const serverMessage = payload.message ?? payload.error

  const message = isUsefulServerMessage(serverMessage)
    ? serverMessage
    : translate(STATUS_KEYS[status] ?? options.fallbackKey ?? 'errors.unknown', { status })

  return build({
    status,
    kind: 'http',
    message,
    retriable: RETRIABLE_STATUSES.has(status),
    fields: normalizeFieldErrors(payload),
    cause: error,
  })
}

/**
 * Joi a través de Boom suele viajar en `validation.keys` o en `details`.
 * Se acepta cualquiera de las dos formas sin romperse si llega otra.
 */
function normalizeFieldErrors(payload) {
  const details = payload?.details ?? payload?.validation?.details
  if (Array.isArray(details)) {
    return details.reduce((acc, item) => {
      const field = Array.isArray(item?.path) ? item.path.join('.') : item?.path || item?.field
      if (!field) return acc
      acc[field] = [...(acc[field] ?? []), item.message ?? '']
      return acc
    }, {})
  }
  const keys = payload?.validation?.keys
  if (Array.isArray(keys) && keys.length > 0) {
    return keys.reduce((acc, key) => ({ ...acc, [key]: [payload.message ?? ''] }), {})
  }
  return null
}

function build({ status, kind, message, retriable, fields = null, cause }) {
  return { __isAppError: true, status, kind, message, retriable, fields, cause }
}

/** ¿El error viene de una petición que el propio panel canceló? */
export function isCanceled(error) {
  return toAppError(error).kind === 'canceled'
}

/** Atajo para pintar el texto de un error sin construir el objeto a mano. */
export function errorMessage(error, fallbackKey) {
  return toAppError(error, { fallbackKey }).message
}
